'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReviewTypeValue } from '@/components/top-navigation/review-dropdown/types';
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2RouteContext } from '@/lib/pr-v2/route-utils';
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { CheckCircle, Edit, ExternalLink, FileSearch, GitBranch, GitCommitHorizontal, Hash, Link2, Maximize2, MessageSquare, MessageSquareCheck, PenLine, RefreshCw, RotateCcw, Send, XCircle } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { useUnmount } from 'usehooks-ts';
import type { Command, CommandPullRequest } from '../types';
import { confirmCommandAction } from '../confirm-command-action';
import { recordRecentPullRequest } from '../recent-pull-requests';
import { useRegisterCommands, useRegisterPaletteTarget } from '../use-commands';
import { prV2Command, prV2ModeRow } from './pr-v2-command-group';
import { usePrV2DiffViewCommands } from './pr-v2-diff-view-commands';
import { PR_V2_REVIEW_OPTIONS } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/comments/pr-v2-review-options';
import { usePrV2PullRequestActions } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/hooks/use-pr-v2-pull-request-actions';
import { useZenModeToggle } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/hooks/use-zen-mode-toggle';
import { usePrV2Refresh } from '@/data/v2/pr/use-pr-v2-refresh';
import { useMergeabilityStatus } from '@/lib/hooks/mergeability-status/use-mergeability-status';
import { isPullRequestAuthor, isPullRequestOpen } from '@/lib/pr-v2/action-helpers';
import { dispatchPrV2TabHandoff } from '@/lib/pr-v2/tab-handoff';
import { parsePrV2Tab, PR_V2_TAB_QUERY_PARAM, PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { useReviewDropdownStore } from '@/lib/stores/review-dropdown-store';
import { useAuth } from '@/providers/auth-provider';
import { ReviewType } from '@/types/github';
type PullRequestState = CommandPullRequest['state'];

/**
 * Reads the state the way the PR header does: the mergeability status first, the summary until
 * that loads.
 */
function readPullRequestState(status: MergeabilityStatus | null, pullRequest: PrV2SummaryResponse['pullRequest']): PullRequestState {
  const isMerged = status ? status.state.merged : pullRequest.merged;
  const isOpen = status ? status.state.prState === 'open' : isPullRequestOpen(pullRequest.state);
  const isDraft = status ? status.draft : pullRequest.draft;
  if (isMerged) {
    return 'merged';
  }
  if (!isOpen) {
    return 'closed';
  }
  return isDraft ? 'draft' : 'open';
}

/**
 * Registers command palette actions scoped to PR V2 pages. The PR layout renders it for members
 * only, inside the chat sidebar's provider.
 */
export function PrV2Commands(t0) {
  const $ = _c(42);
  const {
    owner,
    repo,
    number
  } = t0;
  const searchParams = useSearchParams();
  const {
    user
  } = useAuth();
  const {
    pullRequest,
    previewUrl,
    isUpdatingState,
    openOnGitHub,
    openPreviewDeployment,
    toggleDraft,
    setPullRequestState,
    copyUrl,
    copyPrNumber,
    copyBranchName,
    copyBranchHash,
    copyTitleAsLink
  } = usePrV2PullRequestActions(owner, repo, number);
  const {
    status: mergeabilityStatus
  } = useMergeabilityStatus(owner, repo, number);
  const {
    mutateAsync: refreshPrV2
  } = usePrV2Refresh(owner, repo, number);
  const {
    openWithApproval,
    openWithRequestChanges,
    openWithComment
  } = useReviewDropdownStore();
  const {
    isZenMode,
    toggle: toggleZenMode
  } = useZenModeToggle();
  const diffViewCommands = usePrV2DiffViewCommands();
  let t1;
  if ($[0] !== mergeabilityStatus || $[1] !== pullRequest) {
    t1 = pullRequest ? readPullRequestState(mergeabilityStatus, pullRequest) : null;
    $[0] = mergeabilityStatus;
    $[1] = pullRequest;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const state = t1;
  let t2;
  if ($[3] !== searchParams) {
    t2 = parsePrV2Tab(searchParams?.get(PR_V2_TAB_QUERY_PARAM)) ?? PR_V2_TABS.CHANGES;
    $[3] = searchParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activeTab = t2;
  const t3 = pullRequest?.author.login;
  let t4;
  if ($[5] !== t3 || $[6] !== user) {
    t4 = isPullRequestAuthor(user, t3);
    $[5] = t3;
    $[6] = user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isAuthor = t4;
  let t5;
  if ($[8] !== pullRequest || $[9] !== state) {
    t5 = pullRequest && state ? {
      owner: pullRequest.owner,
      repo: pullRequest.repo,
      number: pullRequest.number,
      title: pullRequest.title ?? `Pull request #${pullRequest.number}`,
      state,
      reviewState: null,
      authorLogin: pullRequest.author.login,
      authorAvatarUrl: pullRequest.author.avatarUrl,
      updatedAt: pullRequest.metadataSummary.updatedAt ?? pullRequest.githubUpdatedAt,
      needsReview: false
    } : null;
    $[8] = pullRequest;
    $[9] = state;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const target = t5;
  useRegisterPaletteTarget(target);
  const recordedKeyRef = useRef(null);
  const userId = user?.id;
  let t6;
  let t7;
  if ($[11] !== target || $[12] !== userId) {
    t6 = () => {
      if (!target || !userId) {
        return;
      }
      const recordKey = `${userId}:${target.owner}/${target.repo}#${target.number}:${target.state}`;
      if (recordedKeyRef.current === recordKey) {
        return;
      }
      recordedKeyRef.current = recordKey;
      recordRecentPullRequest(userId, target);
    };
    t7 = [target, userId];
    $[11] = target;
    $[12] = userId;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      recordedKeyRef.current = null;
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useUnmount(t8);
  let t9;
  if ($[16] !== refreshPrV2) {
    t9 = async () => {
      ;
      try {
        await refreshPrV2();
        toast.success("Refresh queued");
      } catch (t10) {
        const error = t10;
        toast.error(error instanceof Error ? error.message : "Failed to refresh PR cache");
      }
    };
    $[16] = refreshPrV2;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const refreshCache = t9;
  let t10;
  let t11;
  if ($[18] !== activeTab || $[19] !== copyBranchHash || $[20] !== copyBranchName || $[21] !== copyPrNumber || $[22] !== copyTitleAsLink || $[23] !== copyUrl || $[24] !== diffViewCommands || $[25] !== isAuthor || $[26] !== isUpdatingState || $[27] !== isZenMode || $[28] !== openOnGitHub || $[29] !== openPreviewDeployment || $[30] !== openWithApproval || $[31] !== openWithComment || $[32] !== openWithRequestChanges || $[33] !== previewUrl || $[34] !== pullRequest || $[35] !== refreshCache || $[36] !== setPullRequestState || $[37] !== state || $[38] !== toggleDraft || $[39] !== toggleZenMode) {
    t10 = () => {
      const commands = [];
      if (pullRequest && state) {
        const isOpen = state === "open" || state === "draft";
        const canDecide = isOpen && !isAuthor;
        const reviewRowDetails = {
          [ReviewType.APPROVE]: {
            id: "pr-v2-review:approve",
            icon: <CheckCircle />,
            shortcutId: "pr-review-approve",
            action: openWithApproval,
            keywords: ["review", "accept"]
          },
          [ReviewType.COMMENT]: {
            id: "pr-v2-review:comment",
            icon: <MessageSquare />,
            shortcutId: "pr-review-comment",
            action: openWithComment,
            keywords: ["review"]
          },
          [ReviewType.REQUEST_CHANGES]: {
            id: "pr-v2-review:request-changes",
            icon: <Edit />,
            shortcutId: "pr-review-request-changes",
            action: openWithRequestChanges,
            keywords: ["review", "reject"]
          }
        };
        const reviewRows = PR_V2_REVIEW_OPTIONS.filter(option => option.needsOtherAuthor ? canDecide : state !== "closed").map(option_0 => prV2ModeRow({
          ...reviewRowDetails[option_0.type],
          label: option_0.label,
          tag: "pr-v2-review"
        }));
        if (reviewRows.length > 0) {
          commands.push(prV2Command("pr-v2-review", {
            label: "Review...",
            icon: <MessageSquareCheck />,
            shortcutId: "pr-review-open-review-menu",
            enterMode: "pr-v2-review",
            action: _temp
          }), ...reviewRows);
        }
        const canToggleDraft = isOpen && pullRequest.draft === (state === "draft");
        if (canToggleDraft && !isUpdatingState) {
          const isDraft = state === "draft";
          commands.push(prV2Command("pr-v2-toggle-draft", {
            label: isDraft ? "Undraft PR..." : "Convert to draft...",
            icon: isDraft ? <Send /> : <PenLine />,
            shortcutId: isDraft ? "pr-review-publish" : "pr-action-convert-draft",
            action: () => confirmCommandAction(isDraft ? "Mark this pull request ready for review?" : "Convert this pull request to draft?", toggleDraft),
            keywords: ["draft", "publish", "ready for review", "mark ready"]
          }));
        }
        if (state !== "merged" && !isUpdatingState) {
          commands.push(prV2Command("pr-v2-toggle-state", {
            label: isOpen ? "Close pull request..." : "Reopen pull request...",
            icon: isOpen ? <XCircle /> : <RotateCcw />,
            shortcutId: "pr-action-close-reopen",
            action: () => confirmCommandAction(isOpen ? "Close this pull request?" : "Reopen this pull request?", () => setPullRequestState(isOpen ? "closed" : "open")),
            keywords: ["close", "reopen", "state"]
          }));
        }
        commands.push(prV2Command("pr-v2-copy-link", {
          label: "Copy link",
          icon: <Link2 />,
          shortcutId: "pr-copy-link",
          action: () => void copyUrl(),
          keywords: ["copy", "url", "link"]
        }), prV2Command("pr-v2-copy-branch-name", {
          label: "Copy branch name",
          icon: <GitBranch />,
          shortcutId: "pr-copy-branch",
          action: () => void copyBranchName(),
          keywords: ["copy", "branch"]
        }), prV2Command("pr-v2-open-github", {
          label: "Open in GitHub",
          icon: <ExternalLink />,
          shortcutId: "pr-open-github",
          action: openOnGitHub,
          keywords: ["open", "github"]
        }), prV2Command("pr-v2-copy-number", {
          label: "Copy PR number",
          icon: <Hash />,
          action: () => void copyPrNumber(),
          keywords: ["copy", "number", "pull request"]
        }), prV2Command("pr-v2-copy-branch-hash", {
          label: "Copy branch hash",
          icon: <Hash />,
          shortcutId: "pr-copy-hash",
          action: () => void copyBranchHash(),
          keywords: ["copy", "hash", "sha"]
        }), prV2Command("pr-v2-copy-title-as-link", {
          label: "Copy title as link",
          icon: <Link2 />,
          shortcutId: "pr-copy-title-link",
          action: () => void copyTitleAsLink(),
          keywords: ["copy", "title", "markdown", "link"]
        }));
        if (previewUrl) {
          commands.push(prV2Command("pr-v2-open-preview", {
            label: "Open preview deployment",
            icon: <ExternalLink />,
            shortcutId: "pr-open-preview",
            action: openPreviewDeployment,
            keywords: ["preview", "deployment", "vercel"]
          }));
        }
      }
      commands.push(prV2Command("pr-v2-go-to-file", {
        label: "Go to file...",
        icon: <FileSearch />,
        enterMode: "pr-v2-files",
        action: () => {
          if (activeTab === PR_V2_TABS.DISCUSSION) {
            dispatchPrV2TabHandoff(PR_V2_TABS.CHANGES);
          }
          return false;
        },
        keywords: ["file", "open", "jump", "find"]
      }), ...diffViewCommands, prV2Command("pr-v2-refresh-cache", {
        label: "Refresh cache",
        icon: <RefreshCw />,
        action: () => void refreshCache(),
        keywords: ["refresh", "reload", "sync"]
      }), prV2Command("pr-v2-zen-mode", {
        label: isZenMode ? "Disable Zen mode" : "Enable Zen mode",
        icon: <Maximize2 />,
        shortcutId: "pr-view-zen-mode",
        action: toggleZenMode,
        keywords: ["zen", "focus", "distraction free", "fullscreen"]
      }), activeTab === PR_V2_TABS.CHANGES ? prV2Command("pr-v2-switch-to-discussion", {
        label: "Switch to Discussion tab",
        icon: <MessageSquare />,
        action: _temp2,
        keywords: ["discussion", "tab", "switch", "navigate", "timeline", "comments"],
        description: "Open the PR discussion timeline tab.",
        examples: ["show discussion", "open comments", "switch to timeline"],
        safeExecution: "navigation"
      }) : prV2Command("pr-v2-switch-to-changes", {
        label: "Switch to Changes tab",
        icon: <GitCommitHorizontal />,
        action: _temp3,
        keywords: ["changes", "tab", "switch", "navigate", "diff", "files"],
        description: "Open the PR changed files tab.",
        examples: ["show changes", "open diff", "switch to files"],
        safeExecution: "navigation"
      }));
      return commands;
    };
    t11 = [activeTab, copyBranchHash, copyBranchName, copyPrNumber, copyTitleAsLink, copyUrl, diffViewCommands, isAuthor, isUpdatingState, isZenMode, openOnGitHub, openPreviewDeployment, openWithApproval, openWithComment, openWithRequestChanges, previewUrl, pullRequest, refreshCache, setPullRequestState, state, toggleDraft, toggleZenMode];
    $[18] = activeTab;
    $[19] = copyBranchHash;
    $[20] = copyBranchName;
    $[21] = copyPrNumber;
    $[22] = copyTitleAsLink;
    $[23] = copyUrl;
    $[24] = diffViewCommands;
    $[25] = isAuthor;
    $[26] = isUpdatingState;
    $[27] = isZenMode;
    $[28] = openOnGitHub;
    $[29] = openPreviewDeployment;
    $[30] = openWithApproval;
    $[31] = openWithComment;
    $[32] = openWithRequestChanges;
    $[33] = previewUrl;
    $[34] = pullRequest;
    $[35] = refreshCache;
    $[36] = setPullRequestState;
    $[37] = state;
    $[38] = toggleDraft;
    $[39] = toggleZenMode;
    $[40] = t10;
    $[41] = t11;
  } else {
    t10 = $[40];
    t11 = $[41];
  }
  useRegisterCommands(t10, t11);
  return null;
}
function _temp3() {
  return dispatchPrV2TabHandoff(PR_V2_TABS.CHANGES);
}
function _temp2() {
  return dispatchPrV2TabHandoff(PR_V2_TABS.DISCUSSION);
}
function _temp() {
  return false;
}